/* Maison Carona — Cart drawer */
(function () {
  if (window.customElements.get('cart-drawer')) return;

  const ROUTES = (window.routes && window.routes) || {};
  const CART_ADD = ROUTES.cart_add_url || '/cart/add.js';
  const CART_CHANGE = ROUTES.cart_change_url || '/cart/change.js';
  const CART_UPDATE = ROUTES.cart_update_url || '/cart/update.js';
  const CART_GET = ROUTES.cart_url || '/cart';

  function debounce(fn, wait) {
    let t;
    return function (...args) {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(this, args), wait);
    };
  }

  class CartDrawer extends HTMLElement {
    constructor() {
      super();
      this._lastTrigger = null;
      this._refreshing = false;
      this._onDocClick = this._onDocClick.bind(this);
      this._onKeydown = this._onKeydown.bind(this);
      this._onOpenEvent = this._onOpenEvent.bind(this);
      this._onNoteInput = debounce(this._saveNote.bind(this), 600);
    }

    connectedCallback() {
      this.sectionId = this.dataset.sectionId;
      this.addEventListener('click', this._onDocClick);
      this.addEventListener('input', this._onInternalInput.bind(this));
      this.addEventListener('change', this._onInternalChange.bind(this));
      document.addEventListener('keydown', this._onKeydown);
      document.addEventListener('mc:cart:open', this._onOpenEvent);
      this._bindExternalTriggers();
    }

    disconnectedCallback() {
      document.removeEventListener('keydown', this._onKeydown);
      document.removeEventListener('mc:cart:open', this._onOpenEvent);
    }

    /* =================== Public API =================== */
    open(triggerEl) {
      if (this.dataset.state === 'open') return;
      this._lastTrigger = triggerEl || null;
      this.hidden = false;
      this.setAttribute('aria-hidden', 'false');
      // Force reflow before adding open state so transition runs
      // eslint-disable-next-line no-unused-expressions
      this.offsetHeight;
      requestAnimationFrame(() => {
        this.dataset.state = 'open';
        document.body.classList.add('cart-drawer-open');
        const focusable = this.querySelector('.cart-drawer__close');
        if (focusable) focusable.focus({ preventScroll: true });
      });
    }

    close() {
      if (this.dataset.state !== 'open') return;
      this.dataset.state = 'closed';
      this.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('cart-drawer-open');
      const panel = this.querySelector('.cart-drawer__panel');
      const onEnd = (e) => {
        if (e.target !== panel) return;
        panel.removeEventListener('transitionend', onEnd);
        if (this.dataset.state !== 'open') this.hidden = true;
      };
      if (panel) {
        panel.addEventListener('transitionend', onEnd);
        // Fallback in case transitionend doesn't fire
        setTimeout(() => {
          if (this.dataset.state !== 'open') this.hidden = true;
        }, 700);
      } else {
        this.hidden = true;
      }
      if (this._lastTrigger && typeof this._lastTrigger.focus === 'function') {
        this._lastTrigger.focus({ preventScroll: true });
      }
    }

    /* =================== Event wiring =================== */
    _bindExternalTriggers() {
      // Header bouton (data-mc-open-cart) is bound by header script which calls .open()
      // This is a redundant fallback for any [data-mc-open-cart] in the page.
      document.querySelectorAll('[data-mc-open-cart]').forEach((btn) => {
        if (btn.dataset.cartDrawerBound === '1') return;
        btn.dataset.cartDrawerBound = '1';
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          this.open(btn);
        });
      });
    }

    _onOpenEvent(e) {
      this.open(e && e.detail && e.detail.trigger ? e.detail.trigger : null);
    }

    _onKeydown(e) {
      if (e.key === 'Escape' && this.dataset.state === 'open') this.close();
    }

    _onDocClick(e) {
      const closer = e.target.closest('[data-cart-close]');
      if (closer && this.contains(closer)) {
        e.preventDefault();
        this.close();
        return;
      }
      const closeOnNav = e.target.closest('[data-cart-close-on-click]');
      if (closeOnNav && this.contains(closeOnNav)) {
        // let navigation happen, just close drawer state
        this.close();
        return;
      }
      const qtyBtn = e.target.closest('[data-cart-qty]');
      if (qtyBtn && this.contains(qtyBtn)) {
        e.preventDefault();
        this._onQtyClick(qtyBtn);
        return;
      }
      const removeBtn = e.target.closest('[data-cart-remove]');
      if (removeBtn && this.contains(removeBtn)) {
        e.preventDefault();
        this._onRemoveClick(removeBtn);
        return;
      }
      const addBtn = e.target.closest('[data-cart-add]');
      if (addBtn && this.contains(addBtn)) {
        e.preventDefault();
        this._onAddClick(addBtn);
        return;
      }
    }

    _onInternalInput(e) {
      const note = e.target.closest('[data-cart-note]');
      if (note && this.contains(note)) {
        const counter = this.querySelector('[data-cart-note-count]');
        if (counter) counter.textContent = String(note.value.length);
        this._onNoteInput(note.value);
      }
    }

    _onInternalChange(e) {
      const qtyInput = e.target.closest('[data-cart-qty-input]');
      if (qtyInput && this.contains(qtyInput)) {
        const line = qtyInput.closest('[data-cart-line]');
        if (!line) return;
        const key = line.dataset.lineKey;
        const qty = parseInt(qtyInput.value, 10);
        if (Number.isNaN(qty) || qty < 0) {
          qtyInput.value = qtyInput.defaultValue || '1';
          return;
        }
        this._changeQty(key, qty, line);
        return;
      }
      const giftInput = e.target.closest('[data-cart-gift]');
      if (giftInput && this.contains(giftInput)) {
        const line = giftInput.closest('[data-cart-line]');
        if (!line) return;
        this._toggleGift(line.dataset.lineKey, giftInput.checked, line);
      }
    }

    _toggleGift(key, enabled, lineEl) {
      if (lineEl) lineEl.dataset.loading = 'true';
      fetch(CART_CHANGE, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({
          id: key,
          properties: { _gift_wrap: enabled ? 'true' : '' },
          sections: 'cart-drawer',
          sections_url: window.location.pathname,
        }),
      })
        .then((r) => r.json())
        .then((payload) => this._handleSectionsResponse(payload))
        .catch((err) => {
          console.error('[cart-drawer] gift toggle failed', err);
          if (lineEl) lineEl.dataset.loading = 'false';
        });
    }

    /* =================== Cart actions =================== */
    _onQtyClick(btn) {
      const line = btn.closest('[data-cart-line]');
      if (!line) return;
      const key = line.dataset.lineKey;
      const delta = parseInt(btn.dataset.cartQty, 10) || 0;
      const input = line.querySelector('[data-cart-qty-input]');
      const current = input ? parseInt(input.value, 10) || 0 : 0;
      const next = Math.max(0, current + delta);
      // Feedback immédiat
      if (input) input.value = next;
      this._changeQty(key, next, line);
    }

    _onRemoveClick(btn) {
      const line = btn.closest('[data-cart-line]');
      if (!line) return;
      // Feedback immédiat — fade out la ligne avant le refresh
      line.style.transition = 'opacity 200ms ease, transform 200ms ease';
      line.style.opacity = '0';
      line.style.transform = 'translateX(20px)';
      this._changeQty(line.dataset.lineKey, 0, line);
    }

    _onAddClick(btn) {
      const variantId = btn.dataset.variantId;
      if (!variantId) return;
      btn.disabled = true;
      btn.dataset.loading = 'true';
      const original = btn.innerHTML;
      // Feedback immédiat — sans attendre la réponse réseau
      btn.innerHTML = '<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true"><polyline points="20 6 9 17 4 12"/></svg>';
      this._optimisticCountChange(1);
      fetch(CART_ADD, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({
          items: [{ id: parseInt(variantId, 10), quantity: 1 }],
          sections: 'cart-drawer',
          sections_url: window.location.pathname,
        }),
      })
        .then((r) => r.json())
        .then((payload) => this._handleSectionsResponse(payload))
        .catch((err) => {
          console.error('[cart-drawer] add failed', err);
          btn.disabled = false;
          delete btn.dataset.loading;
          btn.innerHTML = original;
          this._optimisticCountChange(-1);
        });
    }

    _optimisticCountChange(delta) {
      const bubble = document.querySelector('[data-mc-cart-count]');
      const current = bubble ? parseInt(bubble.textContent, 10) || 0 : 0;
      const next = Math.max(0, current + delta);
      document.dispatchEvent(
        new CustomEvent('mc:cart:update', { detail: { itemCount: next } })
      );
    }

    _changeQty(key, qty, lineEl) {
      if (lineEl) lineEl.dataset.loading = 'true';
      fetch(CART_CHANGE, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({
          id: key,
          quantity: qty,
          sections: 'cart-drawer',
          sections_url: window.location.pathname,
        }),
      })
        .then((r) => r.json())
        .then((payload) => this._handleSectionsResponse(payload))
        .catch((err) => {
          console.error('[cart-drawer] change failed', err);
          if (lineEl) lineEl.dataset.loading = 'false';
        });
    }

    /* Single round-trip helper: replaces drawer HTML + count from a /cart/* response with sections */
    _handleSectionsResponse(payload) {
      const html = payload && payload.sections && payload.sections['cart-drawer'];
      if (html) this._replaceContent(html);
      if (payload && typeof payload.item_count === 'number') {
        document.dispatchEvent(
          new CustomEvent('mc:cart:update', {
            detail: { itemCount: payload.item_count, totalPrice: payload.total_price },
          })
        );
      } else {
        // /cart/add.js doesn't return item_count at top level — fall back to a cheap GET /cart.js
        this._refreshCount();
      }
    }

    _saveNote(value) {
      fetch(CART_UPDATE, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ note: value }),
      }).catch((err) => console.error('[cart-drawer] note failed', err));
    }

    /* =================== Section refresh =================== */
    _refresh() {
      if (this._refreshing) return Promise.resolve();
      this._refreshing = true;
      return this._fetchSectionHtml()
        .then((html) => {
          if (html) this._replaceContent(html);
          return this._refreshCount();
        })
        .catch((err) => {
          console.warn('[cart-drawer] refresh failed, falling back to reload', err);
          window.location.reload();
        })
        .finally(() => {
          this._refreshing = false;
        });
    }

    _fetchSectionHtml() {
      const sectionName = 'cart-drawer';
      const here = window.location.pathname + window.location.search;
      const sep = here.indexOf('?') === -1 ? '?' : '&';
      const url = `${here}${sep}sections=${sectionName}`;
      return fetch(url, { headers: { 'Accept': 'application/json' } })
        .then((r) => {
          if (!r.ok) throw new Error('section api http ' + r.status);
          return r.json();
        })
        .then((data) => {
          const html = data && data[sectionName];
          if (html) return html;
          // Fallback : fetch la page courante en HTML et parse le <cart-drawer>
          return fetch(here, { headers: { 'Accept': 'text/html' } })
            .then((r) => r.text())
            .then((text) => {
              const match = text.match(/<cart-drawer[\s\S]*?<\/cart-drawer>/);
              return match ? match[0] : null;
            });
        });
    }

    _replaceContent(html) {
      const tmp = document.createElement('div');
      tmp.innerHTML = html;
      const newRoot = tmp.querySelector('cart-drawer') || tmp;
      this.innerHTML = newRoot.innerHTML;
      if (newRoot.dataset && newRoot.dataset.cartEmpty) {
        this.dataset.cartEmpty = newRoot.dataset.cartEmpty;
      }
    }

    _refreshCount() {
      return fetch(`${CART_GET}.js`, { headers: { 'Accept': 'application/json' } })
        .then((r) => r.json())
        .then((cart) => {
          document.dispatchEvent(
            new CustomEvent('mc:cart:update', {
              detail: { itemCount: cart.item_count, totalPrice: cart.total_price },
            })
          );
        });
    }
  }

  window.customElements.define('cart-drawer', CartDrawer);
})();
